import React from "react";
import { safeHref } from "@/lib/utils";

/* Minimal, safe markdown for chat replies: no HTML is ever injected (no dangerouslySetInnerHTML),
   only text nodes and React elements. Supports paragraphs, line breaks, #/##/### headings,
   - / * bullets, 1. numbered lists, **bold**, *italic*, `code`, and [text](http(s) url) source links
   (web-search citations); any other link scheme stays plain text. */
const INLINE = /(\[[^\]\n]+\]\([^)\s]+\)|\*\*[^*]+\*\*|`[^`]+`|\*[^*\n]+\*)/g;
const LINK = /^\[([^\]\n]+)\]\(([^)\s]+)\)$/;

const inline = (text) =>
  String(text).split(INLINE).filter(Boolean).map((part, i) => {
    const link = part.match(LINK);
    if (link) {
      const href = safeHref(link[2]);
      return href
        ? <a key={i} href={href} target="_blank" rel="noopener noreferrer nofollow" className="underline decoration-cyan-400/50 hover:text-cyan-200">{link[1]}</a>
        : <React.Fragment key={i}>{link[1]}</React.Fragment>;
    }
    if (part.startsWith("**") && part.endsWith("**")) return <strong key={i} className="font-semibold">{part.slice(2, -2)}</strong>;
    if (part.startsWith("`") && part.endsWith("`")) return <code key={i} className="px-1 rounded bg-black/30 text-cyan-200 text-[0.92em]">{part.slice(1, -1)}</code>;
    if (part.startsWith("*") && part.endsWith("*") && part.length > 2) return <em key={i}>{part.slice(1, -1)}</em>;
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });

export const Markdown = ({ text }) => {
  const lines = String(text || "").replace(/\r/g, "").split("\n");
  const blocks = [];
  let list = null;   // { ordered, items: [] }

  const flush = () => {
    if (!list) return;
    const Tag = list.ordered ? "ol" : "ul";
    blocks.push(
      <Tag key={`l${blocks.length}`} className={`${list.ordered ? "list-decimal" : "list-disc"} pl-5 space-y-1 my-1.5`}>
        {list.items.map((it, i) => <li key={i}>{inline(it)}</li>)}
      </Tag>
    );
    list = null;
  };

  lines.forEach((raw) => {
    const line = raw.trimEnd();
    const bullet = line.match(/^\s*[-*•]\s+(.*)$/);
    const num = line.match(/^\s*\d+[.)]\s+(.*)$/);
    const head = line.match(/^(#{1,3})\s+(.*)$/);
    if (bullet) {
      if (!list || list.ordered) { flush(); list = { ordered: false, items: [] }; }
      list.items.push(bullet[1]);
      return;
    }
    if (num) {
      if (!list || !list.ordered) { flush(); list = { ordered: true, items: [] }; }
      list.items.push(num[1]);
      return;
    }
    flush();
    if (!line.trim()) return;
    if (head) {
      const size = head[1].length === 1 ? "text-base font-semibold" : "text-sm font-semibold";
      blocks.push(<div key={`h${blocks.length}`} className={`${size} mt-2 mb-1`}>{inline(head[2])}</div>);
      return;
    }
    blocks.push(<p key={`p${blocks.length}`} className="my-1 leading-relaxed">{inline(line)}</p>);
  });
  flush();

  return <div data-testid="markdown-body" className="[&>*:first-child]:mt-0 [&>*:last-child]:mb-0">{blocks}</div>;
};

export default Markdown;
